import type { PartialPropsOf } from '@blaze-runtime/engine-domain';

import { type ParentChildrenStyleOverrides } from '../../canvas-elements/parent-overrides';
import {
  applyParentStyleOverrideRules,
  type ParentStyleOverrideRule,
} from '../../utils/elements/props-builder.utils';
import { PropsBuilder } from '../__base/props-builder';

export class TextAreaPropsBuilder extends PropsBuilder<'textArea'> {
  override setParentStyleProps = (
    elementProps: PartialPropsOf<'textArea'>,
    overrides: ParentChildrenStyleOverrides | null,
    initialValues?: PartialPropsOf<'textArea'>,
  ): this => {
    const font = overrides?.text?.font;
    // Per-prop rules so one manually tweaked knob doesn't block the others
    const rules: ParentStyleOverrideRule<'textArea'>[] = [
      {
        value: font?.fontFamily,
        path: 'content.font.fontFamily',
        patch: { content: { font: { fontFamily: font?.fontFamily } } },
      },
      {
        value: font?.fontWeight,
        path: 'content.font.fontWeight',
        patch: { content: { font: { fontWeight: font?.fontWeight } } },
      },
      {
        value: font?.style,
        path: 'content.font.style',
        patch: { content: { font: { style: font?.style } } },
      },
      {
        value: font?.fontSize,
        path: 'content.font.fontSize',
        patch: { content: { font: { fontSize: font?.fontSize } } },
      },
      {
        value: font?.lineHeight,
        path: 'content.font.lineHeight',
        patch: { content: { font: { lineHeight: font?.lineHeight } } },
      },
      {
        value: font?.letterSpacing,
        path: 'content.font.letterSpacing',
        patch: { content: { font: { letterSpacing: font?.letterSpacing } } },
      },
      {
        value: overrides?.text?.textColor,
        path: 'style.general.textColor',
        patch: { style: { general: { textColor: overrides?.text?.textColor } } },
      },
    ];
    applyParentStyleOverrideRules<'textArea'>(
      (partial) => this.patchProps(partial),
      elementProps,
      overrides,
      initialValues,
      {
        content: {
          font: {
            fontFamily: undefined,
            fontWeight: undefined,
            style: undefined,
            fontSize: undefined,
            lineHeight: undefined,
            letterSpacing: undefined,
          },
        },
        style: { general: { textColor: undefined } },
      },
      rules,
    );
    return this;
  };

  constructor(props: PartialPropsOf<'textArea'> | undefined) {
    super(props, { hasData: true });
  }
}
